/* Created by Interactive 63 - https://interactive63.com */
/**
 * Physical easing curves: damped spring, bounce and elastic. Each factory returns a sampler over
 * 0..1 that is exactly 0 at the start and exactly 1 at the end.
 */
import type { EasingFunction } from './cubic-bezier';

export const DEFAULT_SPRING = { stiffness: 170, damping: 26, mass: 1 };
export const DEFAULT_BOUNCE = { bounces: 3, decay: 0.5 };
export const DEFAULT_ELASTIC = { oscillations: 3, decay: 6 };

const SPRING_STEP_SECONDS = 0.001;
const SPRING_SETTLE_THRESHOLD = 0.0005;
const SPRING_SETTLE_STEPS = 50;
const SPRING_MAX_STEPS = 10_000;

/** Wraps a sampler so the endpoints are exact regardless of floating point error. */
function withExactEnds(fn: EasingFunction): EasingFunction {
  return (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return fn(t);
  };
}

/**
 * A damped spring moving from 0 to 1 from rest, integrated with semi-implicit Euler at 1 ms.
 * The motion is rescaled so the moment it settles maps to t = 1.
 */
export function springCurve(stiffness: number, damping: number, mass: number): EasingFunction {
  const positions: number[] = [0];
  let x = 0;
  let v = 0;
  let settledFor = 0;
  let settleStart = SPRING_MAX_STEPS;

  for (let step = 1; step <= SPRING_MAX_STEPS; step += 1) {
    const a = (-stiffness * (x - 1) - damping * v) / mass;
    v += a * SPRING_STEP_SECONDS;
    x += v * SPRING_STEP_SECONDS;
    positions.push(x);

    if (Math.abs(x - 1) < SPRING_SETTLE_THRESHOLD && Math.abs(v) < SPRING_SETTLE_THRESHOLD) {
      if (settledFor === 0) settleStart = step;
      settledFor += 1;
      if (settledFor >= SPRING_SETTLE_STEPS) break;
    } else {
      settledFor = 0;
      settleStart = SPRING_MAX_STEPS;
    }
  }

  const lastIndex = positions.length - 1;
  const settleSteps = Math.max(1, Math.min(settleStart, lastIndex));

  return withExactEnds((u) => {
    const position = u * settleSteps;
    const index = Math.min(lastIndex - 1, Math.floor(position));
    const fraction = position - index;
    const from = positions[index] ?? 1;
    const to = positions[index + 1] ?? 1;
    return from + (to - from) * fraction;
  });
}

/**
 * An ease-out bounce made of parabolic arcs: one fall from 0 to 1, then `bounces` rebounds whose
 * heights shrink by `decay` each time. Arc durations scale with the square root of height.
 */
export function bounceCurve(bounces: number, decay: number): EasingFunction {
  const count = Math.max(1, Math.round(bounces));
  const heights: number[] = [];
  const durations = [1];
  for (let i = 1; i <= count; i += 1) {
    const height = decay ** i;
    heights.push(height);
    durations.push(2 * Math.sqrt(height));
  }
  const total = durations.reduce((sum, d) => sum + d, 0);
  const lengths = durations.map((d) => d / total);

  return withExactEnds((t) => {
    if (t < (lengths[0] ?? 1)) {
      const u = t / (lengths[0] ?? 1);
      return u * u;
    }
    let start = lengths[0] ?? 0;
    for (let i = 1; i < lengths.length; i += 1) {
      const length = lengths[i] ?? 0;
      if (t < start + length || i === lengths.length - 1) {
        const u = (t - start) / length;
        return 1 - 4 * (heights[i - 1] ?? 0) * u * (1 - u);
      }
      start += length;
    }
    return 1;
  });
}

/**
 * An elastic ease-out: a decaying cosine around the target, with a small linear correction so
 * the curve lands exactly on 1.
 */
export function elasticCurve(oscillations: number, decay: number): EasingFunction {
  /** The uncorrected decaying oscillation. */
  const g = (t: number): number =>
    1 - Math.exp(-decay * t) * Math.cos(2 * Math.PI * oscillations * t);
  const correction = 1 - g(1);
  return withExactEnds((t) => g(t) + t * correction);
}
